import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCompositionStore } from "../../stores/compositionStore.js";
import { useSongStore } from "../../stores/songStore.js";
import { useBandStore } from "../../stores/bandStore.js";
import { useHistoryStore } from "../../stores/historyStore.js";
import { getSong } from "../../infra/api.js";
import { CommandBar } from "../command-bar/CommandBar.js";
import { Timeline } from "../composition/Timeline.js";
import { Transport } from "../playback/Transport.js";
import { usePlaybackEngine } from "../playback/usePlaybackEngine.js";
import { UndoButton } from "../composition/UndoButton.js";
import { HistoryTree } from "../history/HistoryTree.js";
import { CostBanner } from "../cost/CostBanner.js";
import { DnaReference } from "../dna-reference/DnaReference.js";
import { ExportPanel } from "../export/ExportPanel.js";
import { ClipPanel } from "../export/ClipPanel.js";
import { LiveStage } from "../stage/LiveStage.js";
import { SongSonicTraitsControl } from "./SongSonicTraitsControl.js";
import { Badge } from "../../ui/Badge.js";
import { Button } from "../../ui/Button.js";

export function SongEditorPage(): JSX.Element {
  const { songId } = useParams<{ songId: string }>();
  const connection = useCompositionStore((s) => s.connection);
  const bands = useBandStore((s) => s.bands);
  // Time-travel mode is on whenever a history node is being viewed — the
  // Timeline and SectionVariations switch to a read-only past projection.
  const traveling = useHistoryStore((s) => s.travelEventId !== null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [titleStatus, setTitleStatus] = useState<string>("");
  const [bandId, setBandId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const playback = usePlaybackEngine();

  useEffect(() => {
    if (!songId) return;
    void useCompositionStore
      .getState()
      .bootstrap(songId)
      .catch((e: unknown) => setBootError(e instanceof Error ? e.message : String(e)));
    void useBandStore.getState().fetch();
    void getSong(songId).then(({ song }) => {
      setTitle(song.title);
      setTitleStatus(song.title_status);
      setBandId(song.band_id);
    });
    const poll = setInterval(() => {
      void getSong(songId).then(({ song }) => {
        if (song.title_status !== titleStatus) setTitleStatus(song.title_status);
        if (song.title !== title) setTitle(song.title);
      });
    }, 4000);
    return () => {
      clearInterval(poll);
      useCompositionStore.getState().teardown();
    };
  }, [songId]);

  async function onSaveMeta(): Promise<void> {
    if (!songId) return;
    // An empty title is left for the agent to decide — nothing to persist.
    const t = title?.trim();
    if (!t) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await useSongStore.getState().update(songId, { title: t });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (bootError) {
    return (
      <p
        role="alert"
        className="border border-vinyl/40 bg-vinyl/5 p-4 font-mono text-xs uppercase tracking-widest2 text-vinyl"
      >
        Falha ao carregar: {bootError}
      </p>
    );
  }

  const band = bands.find((b) => b.id === bandId);
  const connTone: "active" | "error" | "neutral" =
    connection === "connected" ? "active" : connection === "reconnecting" ? "error" : "neutral";

  return (
    <div className="flex flex-col gap-8">
      <header className="reveal flex flex-col gap-4 border-b border-border pb-6">
        <div className="flex items-center justify-between">
          <Link
            to={bandId ? `/bands/${bandId}` : "/bands"}
            className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted hover:text-paper"
          >
            ← {band ? band.name : "bandas"}
          </Link>
          <div className="flex items-center gap-3">
            <CostBanner />
            <Badge tone={connTone}>{connection}</Badge>
          </div>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <p className="catalog-num">FAIXA / SESSION {songId?.slice(-6)}</p>
        </div>
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <h1
              className="font-display text-[60px] leading-[0.88] tracking-tight text-paper md:text-[80px]"
              style={{ fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 100, "WONK" 1' }}
            >
              {title ?? <em className="text-ink-muted">sem título</em>}
              {titleStatus === "pending" && (
                <span className="ml-3 align-middle font-mono text-xs uppercase tracking-widest2 text-butter">
                  · agente gerando título…
                </span>
              )}
            </h1>
            <div className="mt-3">
              <Button variant="secondary" onClick={() => setEditing((e) => !e)}>
                {editing ? "fechar" : "editar metadata"}
              </Button>
            </div>
          </div>
        </div>

        {editing && (
          <div className="grid grid-cols-12 gap-4 border border-border bg-tape p-5">
            <label className="col-span-12 flex flex-col gap-1 lg:col-span-8">
              <span className="catalog-num">TÍTULO</span>
              <input
                className="border-0 border-b border-border bg-transparent px-0 py-2 font-display text-xl text-paper outline-none focus:border-paper"
                value={title ?? ""}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="(vazio = agente decide)"
              />
            </label>
            <div className="col-span-12 flex justify-end">
              <Button variant="primary" onClick={() => void onSaveMeta()} disabled={saving}>
                {saving ? "salvando…" : "salvar"}
              </Button>
            </div>
          </div>
        )}
      </header>

      <CommandBar />
      <DnaReference />
      <SongSonicTraitsControl band={band} />
      <Transport
        onPlay={playback.play}
        onPause={playback.pause}
        onSeek={playback.seek}
        currentMs={playback.currentMs}
        totalMs={playback.totalMs}
      />
      <LiveStage band={band} engineRef={playback.engineRef} />
      <HistoryTree />
      <Timeline travelMode={traveling} />
      <UndoButton />
      <ExportPanel />
      <ClipPanel />
    </div>
  );
}
